import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { defaultRouteFor } from "@/components/ProtectedRoute";
import { ArrowRight, Briefcase, User2 } from "lucide-react";

export default function Register() {
  const { user, loading } = useAuth();
  const [role, setRole] = useState("employee");
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to={defaultRouteFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        role,
      };
      if (role === "client") payload.business_name = form.business_name.trim();

      const { data } = await api.post("/auth/signup", payload);
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_user", JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.name}!`);
      // Force reload so AuthContext picks up the new session
      window.location.href = defaultRouteFor(data.user.role);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#fdfcf7]">
      {/* Left hero */}
      <div className="hidden lg:flex flex-col justify-between bg-[#1f4d2e] text-stone-100 p-14 relative overflow-hidden">
        <div className="absolute inset-0 glow-bg" />
        <div className="absolute inset-0 grid-bg opacity-30" />

        <div className="relative z-10 flex items-center gap-4">
          <img
            src="/taskmaster-logo.jpg"
            alt="Taskmaster Philippines"
            className="h-16 w-16 rounded-md object-cover ring-1 ring-stone-100/20"
          />
          <div>
            <div className="font-display text-3xl font-medium tracking-tight leading-none text-stone-50">
              Taskmaster Philippines
            </div>
            <div className="font-display-italic text-sm text-[#d4a82a] mt-2">
              Your Virtual Assistance, Your Virtual services
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-10 max-w-xl">
          <div>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#d4a82a]">Join us</span>
              <span className="gold-rule" />
            </div>
            <h1 className="font-display text-6xl xl:text-7xl font-normal leading-[1.05] tracking-tight text-stone-50">
              Begin your
              <br />
              <span className="font-display-italic text-[#d4a82a]">partnership</span>
            </h1>
            <p className="mt-8 text-stone-300/90 leading-[1.7] text-[15px] max-w-md font-light">
              Whether you're an employer looking for productive team members, or a
              professional ready to be matched with a business — start here in under a minute.
            </p>
          </div>
        </div>

        <div className="relative z-10 text-[10px] tracking-[0.3em] uppercase text-stone-500">
          <span className="text-[#d4a82a]">✦</span>&nbsp; Your account, your terms.
        </div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center p-6 sm:p-12 bg-[#fdfcf7]">
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src="/taskmaster-logo.jpg" alt="Taskmaster Philippines" className="h-12 w-12 rounded-md object-cover ring-1 ring-stone-200" />
            <div>
              <div className="font-display text-lg font-semibold tracking-tight text-[#1f4d2e] leading-tight">
                Taskmaster Philippines
              </div>
              <div className="font-display-italic text-[11px] text-stone-500 leading-tight mt-0.5">
                Your Virtual Assistance, Your Virtual services
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase font-medium text-[#1f4d2e] mb-4">
            <span className="h-px bg-[#d4a82a] w-6" />
            Create account
          </div>
          <h2 className="font-display text-5xl font-normal tracking-tight mb-2 text-[#1f4d2e] leading-none">
            Get <span className="font-display-italic text-[#d4a82a]">started</span>
          </h2>
          <p className="text-stone-600 text-[14px] leading-relaxed mt-4 mb-6 font-light">
            Choose the kind of account you want to create.
          </p>

          {/* Role toggle */}
          <div className="grid grid-cols-2 gap-2 mb-6" data-testid="role-toggle">
            <button
              type="button"
              data-testid="role-employer-button"
              onClick={() => setRole("client")}
              className={`flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-elegant ${
                role === "client"
                  ? "border-[#1f4d2e] bg-[#1f4d2e]/[0.06]"
                  : "border-stone-200 hover:border-stone-300 bg-white"
              }`}
            >
              <Briefcase className={`h-5 w-5 ${role === "client" ? "text-[#1f4d2e]" : "text-stone-500"}`} strokeWidth={1.6} />
              <div className="font-display text-lg leading-none text-stone-900">Employer</div>
              <div className="text-[11px] text-stone-500 leading-snug">
                I hire and track my team's work hours.
              </div>
            </button>
            <button
              type="button"
              data-testid="role-employee-button"
              onClick={() => setRole("employee")}
              className={`flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-elegant ${
                role === "employee"
                  ? "border-[#1f4d2e] bg-[#1f4d2e]/[0.06]"
                  : "border-stone-200 hover:border-stone-300 bg-white"
              }`}
            >
              <User2 className={`h-5 w-5 ${role === "employee" ? "text-[#1f4d2e]" : "text-stone-500"}`} strokeWidth={1.6} />
              <div className="font-display text-lg leading-none text-stone-900">Employee</div>
              <div className="text-[11px] text-stone-500 leading-snug">
                I'm a team member — assign me to a business.
              </div>
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3.5">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Full name</Label>
              <Input
                data-testid="signup-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 rounded-md border-stone-200 bg-white focus-visible:ring-1 focus-visible:ring-[#1f4d2e] focus-visible:border-[#1f4d2e] h-11 transition-elegant"
                placeholder="Maria Santos"
              />
            </div>

            {role === "client" && (
              <div>
                <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Business name</Label>
                <Input
                  data-testid="signup-business-input"
                  required
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  className="mt-2 rounded-md border-stone-200 bg-white focus-visible:ring-1 focus-visible:ring-[#1f4d2e] focus-visible:border-[#1f4d2e] h-11 transition-elegant"
                  placeholder="Acme Outsourcing Co."
                />
              </div>
            )}

            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Email</Label>
              <Input
                data-testid="signup-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 rounded-md border-stone-200 bg-white focus-visible:ring-1 focus-visible:ring-[#1f4d2e] focus-visible:border-[#1f4d2e] h-11 transition-elegant"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Password</Label>
              <Input
                data-testid="signup-password-input"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 rounded-md border-stone-200 bg-white focus-visible:ring-1 focus-visible:ring-[#1f4d2e] focus-visible:border-[#1f4d2e] h-11 transition-elegant"
                placeholder="At least 6 characters"
              />
            </div>

            <Button
              data-testid="signup-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-md bg-[#d4a82a] hover:bg-[#b8902a] text-[#1f4d2e] font-semibold tracking-wide group transition-elegant"
            >
              {submitting ? "Creating account…" : (
                <span className="inline-flex items-center gap-2">
                  Create account <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          {role === "employee" && (
            <p className="text-[11px] text-stone-500 mt-4 leading-relaxed">
              <span className="font-display-italic">Note:</span> Once you register, the Super Admin
              will assign you to a business. Your tracker will work once you're assigned.
            </p>
          )}

          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <p className="text-sm text-stone-600">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-[#1f4d2e] hover:text-[#d4a82a] transition-elegant">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
